import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  Input,
  OnDestroy,
  output,
  signal,
  ViewChild,
  viewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDrawer } from '@angular/material/sidenav';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { intersection, isEqual } from 'lodash';

import { BehaviorSubject, combineLatest, defer, EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { backendMessageOf } from '../../../../../shared/components/preview-file/preview-file.component';

import { Actions, ofActionCompleted, Store } from '@ngxs/store';

import { AuthService, AuthStateAdaptorService, isGuestUser } from '@aiwyn/auth';
import { ConfigService, LaunchDarklyFlag, LaunchDarklyService, LaunchDarklyState } from '@aiwyn/config';
import { FilePreviewService } from '@aiwyn/services';
import {
  AiwynBreakpoint,
  compareStringsIgnoreCase,
  convertTextCase,
  DATA_SELECTOR_INJECTION_TOKEN,
  DEFAULT_SNACKBAR_CONFIG,
  DmsIntegrationApiService,
  DmsIntegrationStateSelectors,
  downloadFileFromUrl,
  getControlValueChanges,
  ignoreNullish,
  isEmpty,
  LoadableHelper,
  parseLocalDate,
  RecursivePartial,
  SnackbarComponent,
  SnackbarType,
  UploadFileJob,
  UploadFileStatus,
  UploadType,
} from '@aiwyn/shared';

import {
  ClientRequestDocumentUploadModel,
  ClientRequestItemFileReference,
  ClientRequestItemStatus,
  ClientRequestStatus,
  RequestDocument,
  RequestItem,
  RequestItemType,
  RequestSection,
  RequestSubmissionFile,
} from '@aiwyn/api';
import {
  BaseCommonClientRequest,
  ClientRequestIdentityHelper,
  CommonClientRequestFactoryService,
  CommonClientRequestsState,
} from '@aiwyn/common/client-requests';

import {
  ClientRequestDocumentFileDisplayStatus,
  ClientRequestsLayout,
  getOriginalBoxFileName,
  REQUEST_ITEM_DUE_DATE_FILTER_OPTIONS,
  RequestItemDueDateFilterOption,
  RequestItemDueDateFilters,
  RequestItemStatusFilterOption,
} from '../../../models';
import { ClientRequestsLayoutService } from '../../../services';
import {
  ClearRequestItemFilterSelection,
  ClearSelectedClientRequestId,
  ClientRequestsState,
  ClientRequestSubmitFileData,
  LoadAnswerActivity,
  LoadDocumentActivity,
  SetRequestItemAsNotApplicable,
  SetSelectedRequestItemId,
  StageFilesForClientRequest,
  SubmitClientRequest,
  SubmitFilesForClientRequest,
  UpdateRequestItemFilterSelection,
} from '../../../state';

import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import {
  ConfirmFlaggedFilesSubmissionComponent,
  ConfirmFlaggedFilesSubmissionData,
} from '../../../../../components/main/modals/confirm-flagged-files-submission/confirm-flagged-files-submission.component';
import {
  ClientRequestSubmissionConfirmationDialogComponent,
  ClientRequestSubmissionConfirmationDialogData,
  SubmissionDialogStatus,
} from '../../client-request-submission-confirmation-dialog/client-request-submission-confirmation-dialog.component';
import { ClientRequestPanelSelectors } from './client-request-panel.state';
import { buildReferenceFilePreviewList } from './client-request-panel.utils';
import { FileDisplay } from './components/file-item/file-item.model';
import { CommentsSelectors } from '../../../components/comments/state/comments.selectors';
import { ITEM_REQUEST_STATUS_LABEL } from '../../../../../utility/status-labels-dictionaries';

@Component({
  selector: 'awn-client-request-panel',
  templateUrl: './client-request-panel.component.html',
  styleUrls: ['./client-request-panel.component.scss', '../../../styles/client-requests.scss'],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'client-request-panel',
    },
  ],
  standalone: false,
})
export class ClientRequestPanelComponent implements AfterViewInit, OnDestroy {
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);
  isLoading: boolean = true;
  @Input() instance!: BaseCommonClientRequest<ClientRequestDocumentUploadModel>;
  @Input() isUsingSecureLink = false;
  canShowCompletionDialog = input<boolean>(true);
  submissionCompleted = output<void>();
  sidebarScrollContent = viewChild<ElementRef<HTMLDivElement>>('sidebarScrollContent');
  @ViewChild(MatDrawer) drawer!: MatDrawer;

  protected showFooterBoxShadow$ = new BehaviorSubject<boolean>(false);

  // Mobile responsive state
  protected readonly isMobile$ = this._breakpointObserver.observe([AiwynBreakpoint.SIDEBAR_POPOVER]).pipe(
    map(({ matches }) => matches),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // Hook ngOnDestroy Observable
  private readonly destroy$ = new Subject<void>();

  // Global Search RL field
  protected readonly searchControl = new FormControl<string>('');
  protected readonly showActivitySidebar = signal<boolean>(false);
  protected acceptedFileTypes: string[] = [];

  isFullLayout$ = this._layoutService.layout$.pipe(
    map((layout) => layout === ClientRequestsLayout.FULL),
    shareReplay(1),
  );

  isReadonly$ = defer(() => this.instance.status$).pipe(
    ignoreNullish(),
    map((status) => status === ClientRequestStatus.DONE), //|| status === ClientRequestStatus.IN_REVIEW),
    shareReplay(1),
  );

  //#region Client Request properties
  projectName$ = defer(() => this._factoryService.getClientRequestGroups$(this.instance.groupIdentity$)).pipe(
    ignoreNullish(),
    switchMap((group) => group.typeClass$),
    switchMap((group) => group.name$),
    shareReplay(1),
  );

  requestName$ = defer(() => this.instance.name$).pipe(ignoreNullish(), shareReplay(1));

  requestDescription$ = defer(() => this.instance.data$).pipe(
    map((data) => data?.description),
    shareReplay(1),
  );

  clientName$ = defer(() => this.instance.client$).pipe(
    switchMap((client) => client.typeClass$),
    switchMap((client) => client.name$),
    shareReplay(1),
  );

  sections = signal<RequestSection[]>([]);
  sections$ = defer(() => this.instance.typeClass$).pipe(
    switchMap((typeClass) => typeClass.sections$),
    // Refactor this if BaseCommonClientRequest is changed to a signal based implementation
    tap((sections) => this.sections.set(sections)),
    shareReplay(1),
  );

  // Computed property to get all reference files from all request items
  allReferenceFiles$ = this.sections$.pipe(
    map((sections) => {
      const allFiles: ClientRequestItemFileReference[] = [];
      sections?.forEach((section) => {
        section.items?.forEach((item) => {
          if (item.clientRequestItemFileReferences?.length) {
            allFiles.push(...item.clientRequestItemFileReferences);
          }
        });
      });
      // Remove duplicates by file ID using Set for O(n) performance
      const seenFileIds = new Set<number>();
      return allFiles.filter((file) => {
        if (seenFileIds.has(file.fileId)) {
          return false;
        }
        seenFileIds.add(file.fileId);
        return true;
      });
    }),
    shareReplay(1),
  );
  //#endregion

  protected hasPendingFiles$ = this._store.select(ClientRequestsState.getSlices.stagedFileJobIds).pipe(
    switchMap((stagedFileJobIds) => {
      if (!stagedFileJobIds.length) {
        return of(false);
      }

      return combineLatest(
        stagedFileJobIds.map((jobId) => this._store.select(DmsIntegrationStateSelectors.getUploadFileJobByJobId(jobId))),
      ).pipe(
        map((jobs) =>
          jobs.some(
            (job) =>
              (job?.status !== UploadFileStatus.ERROR && job?.status !== UploadFileStatus.LOADED) || job.data.status === ClientRequestDocumentFileDisplayStatus.CATEGORIZING
          ),
        ),
      );
    }),
  );

  protected answerActivity$ = combineLatest([this._store.select(ClientRequestsState.getItemActivityViewModels), this.sections$]).pipe(
    map(([activityViewModels, sections]) => {
      const requestItems = sections?.flatMap((section) => section.items);

      // Just add itemName from sections if not already present - selector already handles normalization
      return activityViewModels.map((activity) => ({
        ...activity,
        itemName: activity.itemName || requestItems?.find((item) => item.id === activity.itemId)?.name,
      }));
    }),
  );

  // Toggle for hiding not applicable items
  protected readonly hideNotApplicable = signal<boolean>(false);
  protected readonly hideNotApplicable$ = new BehaviorSubject<boolean>(false);
  protected readonly isActivityLoading = signal<boolean>(false);

  protected rejectedRequests$: Observable<RequestItem[]> = this.sections$.pipe(
    map((sections) => {
      const requestItems = sections.flatMap((section) => section.items);

      // Rejected and approved request_items that have a note to it
      return requestItems.filter((requestItem) => requestItem.status === ClientRequestItemStatus.REJECTED);
    }),
  );

  activeFilters$ = this._store.select(ClientRequestsState.getSelectedRequestItemFilters);

  protected filteredSections$ = combineLatest([
    this.sections$,
    defer(() => this.isClientRequestsUpdatedFiltersEnabled$).pipe(
      switchMap((isEnabled) => (isEnabled ? this.activeFilters$ : of(undefined))),
    ),
    getControlValueChanges(this.searchControl, true).pipe(map<string, string>((value) => value.toLowerCase())),
    this.hideNotApplicable$,
  ]).pipe(
    map(([sections, filters, searchValue, hideNotApplicable]) => {
      const filteredSections = sections.map((section) => {
        return {
          ...section,
          items: section.items.filter((requestItem) => {
            // Status filter
            if (filters?.status && !filters.status[requestItem.status]) {
              return false;
            }

            // Not applicable filter
            if (hideNotApplicable && this.isRequestItemNotApplicable(requestItem.id)) {
              return false;
            }

            // Due date filter
            if (filters?.dueDate && !ClientRequestPanelComponent._doesItemPassDueDateFilter(requestItem, filters.dueDate)) {
              return false;
            }

            // Search filter
            return (
              (requestItem.name ?? '').toLowerCase().includes(searchValue) ||
              (requestItem.description ?? '').toLowerCase().includes(searchValue)
            );
          }),
        } as RequestSection;
      });

      return filteredSections.filter((section) => section.items.length > 0);
    }),
    finalize(() => {
      this.isLoading = false;
    }),
    takeUntil(this.destroy$),
  );

  /**
   * Can only call this when dueDateFilters is defined.
   * Determines if a request item passes the due date filter based on calendar-based logic:
   * - overdue: due date was yesterday or before (not today)
   * - dueToday: due date is the same calendar day as today
   * - dueThisWeek: due date is between now and before midnight of the upcoming Sunday (includes dueToday, excludes overdue)
   * @param item
   * @param dueDateFilters
   * @private
   */
  private static _doesItemPassDueDateFilter(item: RequestItem, dueDateFilters: RecursivePartial<RequestItemDueDateFilters>): boolean {
    if (!item.dueDate) {
      return false;
    }

    const now = new Date();
    const itemDueDate = parseLocalDate(item.dueDate);

    // Normalize dates to start of day for calendar-based comparisons
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const itemDueDateStart = new Date(itemDueDate.getFullYear(), itemDueDate.getMonth(), itemDueDate.getDate());

    // Calculate start of upcoming Sunday
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysUntilSunday = dayOfWeek === 0 ? 7 : 7 - dayOfWeek; // If today is Sunday, next Sunday is 7 days away
    const startOfUpcomingSunday = new Date(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate() + daysUntilSunday);

    // Overdue: due date was yesterday or before (strictly before today)
    const isOverdue = itemDueDateStart.getTime() < todayStart.getTime();

    // Due today: due date is the same calendar day
    const isDueToday = itemDueDateStart.getTime() === todayStart.getTime();

    // Due this week: due date is from today up to (but not including) midnight of the upcoming Sunday
    // This includes "dueToday" but excludes "overdue"
    const isDueThisWeek =
      itemDueDateStart.getTime() >= todayStart.getTime() && itemDueDateStart.getTime() < startOfUpcomingSunday.getTime();

    return (
      !!(dueDateFilters.overdue && isOverdue) ||
      !!(dueDateFilters.dueToday && isDueToday) ||
      !!(dueDateFilters.dueThisWeek && isDueThisWeek)
    );
  }

  // Request counters for display
  protected totalRequestCount$ = this.sections$.pipe(
    map((sections) => sections.reduce((total, section) => total + section.items.length, 0)),
    shareReplay(1),
  );

  protected visibleRequestCount$ = this.filteredSections$.pipe(
    map((sections) => sections.reduce((total, section) => total + section.items.length, 0)),
    shareReplay(1),
  );

  protected readyToSubmit$ = combineLatest([
    this.sections$,
    this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_IN_REVIEW_ITEMS_EDITING),
  ]).pipe(
    map(([sections, isInReviewEditingEnabled]) => {
      const requestItems = sections.flatMap((section) => section.items);

      return requestItems.filter((requestItem) => {
        if (!requestItem.isReadyToSubmit) return false;
        if (requestItem.status === ClientRequestItemStatus.DONE) return false;

        // If feature flag is enabled, include IN_REVIEW items
        if (isInReviewEditingEnabled) {
          return true;
        }

        // If feature flag is disabled, exclude IN_REVIEW items (original behavior)
        return requestItem.status !== ClientRequestItemStatus.IN_REVIEW;
      });
    }),
  );

  protected readyToSubmitCount$ = this.readyToSubmit$.pipe(map((readyToSubmitRequestItems) => readyToSubmitRequestItems.length));

  // Count of uncategorized submission files created after the last submission
  // This is independent of item status and enables the files submit button
  protected updatedSubmissionFilesCount$ = combineLatest([
    this._store.select(ClientRequestsState.getUncategorizedFileDisplays),
    this._store.select(ClientRequestsState.getSelectedClientRequestDetails),
  ]).pipe(
    map(([uncategorizedFiles, details]) => {
      if (!uncategorizedFiles?.length) return 0;

      const submittedAt = details?.submittedAt ? new Date(details.submittedAt).getTime() : null;

      // Filter files that were created after the last submission
      const updatedFiles = uncategorizedFiles.filter((file) => {
        // If no submittedAt on request, all files are new
        if (!submittedAt) return true;
        // If file has no createdAt, consider it new
        if (!file.createdAt) return true;
        // File needs submission if created after the last submission
        return new Date(file.createdAt).getTime() > submittedAt;
      });

      return updatedFiles.length;
    }),
    shareReplay(1),
  );

  protected isSubmissionButtonLoading = signal(false);

  protected submittedCount = signal(0);

  protected showMobileSubmitButton$ = combineLatest([
    this.isMobile$,
    this.readyToSubmitCount$,
    this.updatedSubmissionFilesCount$,
    toObservable(this.showActivitySidebar),
  ]).pipe(
    map(([isMobile, readyToSubmitCount, updatedSubmissionFilesCount, showActivitySidebar]) => {
      return isMobile && ((readyToSubmitCount || 0) > 0 || (updatedSubmissionFilesCount || 0) > 0) && !showActivitySidebar;
    }),
  );

  // Check if there are any invalid IN_REVIEW items that are ready to submit
  // With the new file date-based logic, IN_REVIEW items are only included if they have new files,
  // so this validation is no longer needed for IN_REVIEW items when the feature flag is enabled
  protected hasInvalidInReviewItems$ = of(false);

  protected isFileFlaggingEnabled = toSignal(this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_FILE_FLAGGING), {
    initialValue: false,
  });

  // LaunchDarkly flag for IN_REVIEW items editing
  protected isInReviewEditingEnabled = toSignal(this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_IN_REVIEW_ITEMS_EDITING), {
    initialValue: false,
  });

  // Item restriction signals
  private isItemRestrictionsEnabled = toSignal(
    this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_REQUEST_ITEM_RESTRICTIONS),
    { initialValue: false },
  );
  private contactId = toSignal(this._authService.getUser().pipe(map(user => user?.getContactId())));

  protected isRequestItemRestricted(item: { restricted?: boolean; assignees?: { contactId: number }[] }): boolean {
    return this.isItemRestrictionsEnabled() && item?.restricted === true && !item.assignees?.some(a => a.contactId === this.contactId());
  }

  // Get flagged files for submission validation
  protected flaggedFiles$ = this._store.select(ClientRequestsState.getFlaggedFilesWithRequestItems);

  // Computed Signal Pattern: Reactive N/A items with performant lookups
  private notApplicableItemsSignal = toSignal(this._store.select(ClientRequestsState.getNotApplicableItemsForSelectedRequest), {
    initialValue: [],
  });

  // Computed Signal Pattern: Reactive N/A notes with performant lookups
  private notApplicableDataSignal = toSignal(
    this._store.select(ClientRequestsState.getSlices.notApplicableItems).pipe(
      map((notApplicableItems) => {
        const selectedRequestId = this._store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);
        return notApplicableItems[selectedRequestId] || [];
      }),
    ),
    { initialValue: [] },
  );

  protected jobId$ = defer(() => this.instance.groupIdentity$).pipe(
    map((identity) => ClientRequestIdentityHelper.parseGroupIdentity(identity)),
    map((parsedIdentity) => parsedIdentity.id),
  );

  uncategorizedFileDisplays = computed(() => {
    const fileDisplays = this._store.selectSignal(ClientRequestsState.getUncategorizedFileDisplays)();
    const metadataLoadable = this._store.selectSignal(ClientRequestsState.getSelectedClientRequestMetadataLoadable)();
    if (!metadataLoadable || !fileDisplays) {
      return [];
    }

    const metadata = LoadableHelper.getData(metadataLoadable);
    const filteredFiles: RequestSubmissionFile[] = metadata?.files?.filter(({ fileId }) => fileDisplays.some((file) => file.id === fileId));

    if (!filteredFiles || filteredFiles.length === 0) {
      return [];
    }

    // Map files using submission file data from selector
    const mappedResults = filteredFiles.map((itemSubmissionFile) => {
      const originalFileName = getOriginalBoxFileName(itemSubmissionFile?.file?.name);
      const fileExtensionIndex = originalFileName?.lastIndexOf('.');
      const maskedFileExtension = itemSubmissionFile.file['extension'];

      return {
        ...(itemSubmissionFile.file ?? {}),
        wasAutoCategorized: false,
        name: originalFileName?.slice(0, fileExtensionIndex),
        extension: maskedFileExtension ?? originalFileName?.slice(fileExtensionIndex + 1),
        isFlagged: false,
        submissionFileId: itemSubmissionFile.id,
        flaggedAt: null,
        createdByContact: null,
        createdAt: null,
        version: 1,
      } as FileDisplay;
    });

    mappedResults.sort((a, b) => compareStringsIgnoreCase(a.name, b.name));
    return mappedResults;
  });

  protected selectedRequestItemStatusFilterDetails$ = this._store.select(ClientRequestsState.getSelectedRequestItemStatusFilterDetails);

  protected stagedFilesForThisDocumentIds = signal<string[]>([]);

  public showUncategorizedFilesSection$ = combineLatest([
    this._authService.getUser(),
    this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.UNCATEGORIZED_FILES_SECTION_ENABLED),
  ]).pipe(map(([user, isUncategorizedFilesSectionEnabled]) => !isGuestUser(user) && isUncategorizedFilesSectionEnabled));

  protected enableReferenceFiles$ = this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_REFERENCE_FILES);

  protected selectedRequestItemId = toSignal(this._store.select(ClientRequestsState.getSlices.selectedRequestItemId));
  protected haveUnreadComments = toSignal(this._store.select(CommentsSelectors.hasUnreadComments));
  protected commentSectionIds = computed(() => {
    const selectedId = this.selectedRequestItemId();
    const sections = this.sections() ?? [];
    return selectedId ? [selectedId] : sections.flatMap((section) => section.items?.map((item) => item.id).filter((id) => !!id));
  });

  constructor(
    private readonly _store: Store,
    private readonly _actions$: Actions,
    private readonly _authService: AuthService,
    private readonly _factoryService: CommonClientRequestFactoryService,
    private readonly _layoutService: ClientRequestsLayoutService,
    private readonly _dialog: MatDialog,
    private readonly _configService: ConfigService,
    private readonly _authStateAdaptor: AuthStateAdaptorService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly _dmsIntegrationApiService: DmsIntegrationApiService,
    private readonly _filePreviewService: FilePreviewService,
    private readonly _breakpointObserver: BreakpointObserver,
    private readonly _snackbar: MatSnackBar,
  ) {
    this.acceptedFileTypes = this._configService.clientRequestFileTypeWhitelist;

    combineLatest([this._store.select(ClientRequestPanelSelectors.getUploadedFileJobIds), toObservable(this.stagedFilesForThisDocumentIds)])
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev[0], curr[0]) && isEqual(prev[1], curr[1])),
        map(([files, stagedFileJobIds]) => intersection(files, stagedFileJobIds)),
        filter((idsToRemove) => idsToRemove.length > 0),
        takeUntil(this.destroy$),
      )
      .subscribe((idsToRemove) => {
        return this.stagedFilesForThisDocumentIds.update((ids) => ids.filter((id) => !idsToRemove.includes(id)));
      });
  }

  protected readonly enableMarkNotApplicable$ = this._launchDarklyService
    .getFeatureFlag(LaunchDarklyFlag.ENABLE_MARK_NOT_APPLICABLE)
    .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

  protected readonly enableCommentsInActivitySidebar = toSignal(
    this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_REQUEST_THREADED_COMMENTS),
  );

  ngAfterViewInit(): void {
    new ResizeObserver(([sidebarScrollContentElement]) => {
      const { scrollHeight, offsetHeight } = sidebarScrollContentElement.target as HTMLDivElement;
      this.showFooterBoxShadow$.next(scrollHeight > offsetHeight);
    }).observe(this.sidebarScrollContent().nativeElement);

    this.sections$
      .pipe(
        take(1),
        switchMap((sections) => {
          const allItems = sections.flatMap((section) => section.items);

          const questionnaireItemIds = allItems
            .filter((item) => item.type === RequestItemType.QUESTIONNAIRE)
            .map((item) => item.id);

          const documentItemIds = (
            allItems.filter((item) => item.type === RequestItemType.DOCUMENT_REQUEST) as RequestDocument[]
          ).map((item) => item.id);

          const listeners: Observable<unknown>[] = [];

          if (questionnaireItemIds.length > 0) {
            this.isActivityLoading.set(true);
            listeners.push(this._actions$.pipe(ofActionCompleted(LoadAnswerActivity), take(1)));
          }

          if (documentItemIds.length > 0) {
            this.isActivityLoading.set(true);
            listeners.push(this._actions$.pipe(ofActionCompleted(LoadDocumentActivity), take(1)));
          }

          if (questionnaireItemIds.length > 0) {
            this._store.dispatch(new LoadAnswerActivity(questionnaireItemIds));
          }

          if (documentItemIds.length > 0) {
            this._store.dispatch(new LoadDocumentActivity(documentItemIds));
          }

          return listeners.length > 0 ? forkJoin(listeners) : EMPTY;
        }),
        debounceTime(50),
        switchMap(() => this.answerActivity$),
        withLatestFrom(this.isMobile$),
        takeUntil(this.destroy$),
        take(1),
      )
      .subscribe(([combinedActivity, isMobile]) => {
        this.isActivityLoading.set(false);
        if (!isMobile && !this.enableCommentsInActivitySidebar()) {
          this.showActivitySidebar.set((combinedActivity as unknown[]).length > 0);
        }
      });

    // Subscribe to filteredSections$ to trigger the finalize operator
    this.filteredSections$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      // This subscription will trigger the finalize operator
    });
  }

  submit(): void {
    this.isSubmissionButtonLoading.set(true);
    this._store
      .select(ClientRequestsState.getSlices.stagedFileJobIds)
      .pipe(
        filter((jobIds) => isEmpty(jobIds)),
        take(1),
        switchMap(() => this.checkFlaggedFilesConfirmation()),
        switchMap((confirmed) => (confirmed ? this.dispatchSubmission() : of([]))),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSubmissionButtonLoading.set(false);
          this.submissionCompleted.emit();
        }),
      )
      .subscribe();
  }

  private checkFlaggedFilesConfirmation(): Observable<boolean> {
    if (!this.isFileFlaggingEnabled()) {
      return of(true);
    }

    return this.flaggedFiles$.pipe(
      take(1),
      switchMap((flaggedFiles) => {
        if (flaggedFiles.length > 0) {
          return this.openFlaggedFilesConfirmationDialog({
            flaggedFilesCount: flaggedFiles.length,
          }).afterClosed();
        }
        return of(true);
      }),
    );
  }

  openFlaggedFilesConfirmationDialog(
    data: ConfirmFlaggedFilesSubmissionData,
  ): MatDialogRef<ConfirmFlaggedFilesSubmissionComponent, boolean> {
    return this._dialog.open<ConfirmFlaggedFilesSubmissionComponent, ConfirmFlaggedFilesSubmissionData>(
      ConfirmFlaggedFilesSubmissionComponent,
      { data },
    );
  }

  private dispatchSubmission(): Observable<RequestItem[]> {
    return this._store.dispatch(new SubmitClientRequest()).pipe(
      switchMap(() => this.readyToSubmit$),
      take(1),
      tap((requestItems) => {
        this.submittedCount.set(requestItems.length);
        if (this.canShowCompletionDialog()) {
          this.openSubmissionConfirmationDialog({ status: SubmissionDialogStatus.SUCCESS });
        }
      }),
      catchError(() => {
        this.openSubmissionConfirmationDialog({ status: SubmissionDialogStatus.ERROR });
        return of(null);
      }),
      takeUntil(this.destroy$),
    );
  }

  openSubmissionConfirmationDialog(
    data: ClientRequestSubmissionConfirmationDialogData,
  ): MatDialogRef<ClientRequestSubmissionConfirmationDialogComponent, ClientRequestSubmissionConfirmationDialogData> {
    return this._dialog.open<ClientRequestSubmissionConfirmationDialogComponent, ClientRequestSubmissionConfirmationDialogData>(
      ClientRequestSubmissionConfirmationDialogComponent,
      { data },
    );
  }

  close(): void {
    this._store.dispatch([new ClearSelectedClientRequestId()]);
  }

  /**
   * Toggle mobile drawer visibility
   */
  toggleDrawer(): void {
    this.drawer?.toggle();
  }

  /**
   * Close drawer (for backdrop click)
   */
  closeDrawer(): void {
    this.drawer?.close();
  }

  previewReferenceFile(fileRef: ClientRequestItemFileReference): void {
    this.allReferenceFiles$
      .pipe(
        takeUntil(this.destroy$),
        take(1),
        tap((refs) => {
          const { files, initialIndex } = buildReferenceFilePreviewList(refs, fileRef);
          this._filePreviewService.previewMany(files, initialIndex);
        }),
      )
      .subscribe();
  }

  downloadReferenceFile(fileRef: ClientRequestItemFileReference): void {
    this._dmsIntegrationApiService
      .getDownloadUrl({ files: [fileRef.fileId], isMultipleDownload: false })
      .pipe(
        takeUntil(this.destroy$),
        take(1),
        tap((url) => {
          downloadFileFromUrl({ href: url, download: fileRef.file.name, target: '_blank' });
        }),
        catchError((error) => {
          const message = backendMessageOf(error) ?? 'Download failed';
          this._snackbar.openFromComponent(SnackbarComponent, {
            ...DEFAULT_SNACKBAR_CONFIG,
            data: { message, type: SnackbarType.ERROR },
          });
          return EMPTY;
        }),
      )
      .subscribe();
  }

  toggleActivitySidebar(forceOpen?: boolean): void {
    if (forceOpen) {
      this.showActivitySidebar.set(true);
    } else {
      this.showActivitySidebar.set(!this.showActivitySidebar());
    }
  }

  /**
   * Handle go back action from mobile sidebar header
   * Closes the activity sidebar
   */
  handleGoBack(): void {
    this.showActivitySidebar.set(false);
  }

  /**
   * Handle show all action from mobile sidebar header
   * Closes the sidebar and clears filters to show all items in main view
   */
  handleShowAll(): void {
    // show all entities and not only a single one
    this._store.dispatch(new SetSelectedRequestItemId(undefined));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isRequestItemNotApplicable = (requestItemId: number): boolean => {
    return this.notApplicableItemsSignal().includes(requestItemId);
  };

  getExistingNote = (requestItemId: number): string | null => {
    return this.notApplicableDataSignal().find((item) => item.itemId === requestItemId)?.note || null;
  };

  onNotApplicableChange(requestItemId: number, changeData: { isNotApplicable: boolean; notApplicableReason?: string }): void {
    this._store.dispatch(new SetRequestItemAsNotApplicable(requestItemId, changeData.isNotApplicable, changeData.notApplicableReason));
  }

  toggleHideNotApplicable(event: MatSlideToggleChange): void {
    this.hideNotApplicable.set(event.checked);
    this.hideNotApplicable$.next(event.checked);
  }

  onUploadFilesStart(files: UploadFileJob[]): void {
    // stage files (will be uploaded to our DB once DMS upload succeeds)
    const jobIds = files.map((f) => f.jobId);
    this.stagedFilesForThisDocumentIds.set([...this.stagedFilesForThisDocumentIds(), ...jobIds]);
    this._store.dispatch(new StageFilesForClientRequest(jobIds));
  }

  onUploadFilesComplete(files: UploadFileJob[], skipCategorization = true): void {
    // submit files
    const marshaledFiles: ClientRequestSubmitFileData[] = files
      .filter((file) => file.status === UploadFileStatus.LOADED)
      .map((file) => ({
        jobId: file.jobId,
        documentIds: [] as number[],
        skipCategorization,
      }));

    if (marshaledFiles.length) {
      this._store.dispatch(new SubmitFilesForClientRequest(marshaledFiles));
    }
  }

  toggleItemFilter<
    FilterType extends 'status' | 'dueDate',
    FilterOption extends FilterType extends 'status' ? RequestItemStatusFilterOption : RequestItemDueDateFilterOption,
  >(isApplied: boolean, filterType: FilterType, filterOption: FilterOption): void {
    if (!this._store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_REQUESTS_UPDATED_FILTERS))) {
      return;
    }

    this._store.dispatch(
      new UpdateRequestItemFilterSelection(isApplied, [
        ClientRequestIdentityHelper.parseRequestIdentity(this.instance.identity).id,
        filterType,
        filterOption as never, // bro don't @ me...
      ]),
    );
  }

  protected readonly RequestItemType = RequestItemType;
  protected readonly ClientRequestItemStatus = ClientRequestItemStatus;
  protected readonly dueDateFilterOptions$ = combineLatest([this.activeFilters$, defer(() => this.instance.requestItems$)]).pipe(
    map(([filters, items]) => {
      const selectedFilters = filters?.dueDate;
      const itemDueDates = items.filter((item) => item.dueDate).map((item) => parseLocalDate(item.dueDate));

      return REQUEST_ITEM_DUE_DATE_FILTER_OPTIONS.map((option) => ({
        value: option,
        label: convertTextCase(option, 'camel', 'title'),
        active: selectedFilters?.[option],
        disabled: !ClientRequestPanelComponent._doAnyDueDatesMatchFilter(itemDueDates, option),
      }));
    }),
  );

  /**
   * Determines if any of the given due dates match the specified filter option.
   * Uses calendar-based logic consistent with _doesItemPassDueDateFilter.
   */
  private static _doAnyDueDatesMatchFilter(itemDueDates: Date[], filterOption: RequestItemDueDateFilterOption): boolean {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate start of upcoming Sunday
    const dayOfWeek = now.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 7 : 7 - dayOfWeek;
    const startOfUpcomingSunday = new Date(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate() + daysUntilSunday);

    return itemDueDates.some((dueDate) => {
      const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());

      switch (filterOption) {
        case 'overdue':
          // Due date was yesterday or before (strictly before today)
          return dueDateStart.getTime() < todayStart.getTime();
        case 'dueToday':
          // Due date is the same calendar day
          return dueDateStart.getTime() === todayStart.getTime();
        case 'dueThisWeek':
          // Due date is from today up to (but not including) midnight of the upcoming Sunday
          return dueDateStart.getTime() >= todayStart.getTime() && dueDateStart.getTime() < startOfUpcomingSunday.getTime();
        default:
          return false;
      }
    });
  }

  clearFilters(): void {
    const requestId = ClientRequestIdentityHelper.parseRequestIdentity(this.instance.identity).id;
    this._store.dispatch(new ClearRequestItemFilterSelection(requestId));
  }

  isClientRequestsUpdatedFiltersEnabled$ = this._launchDarklyService.getFeatureFlag(
    LaunchDarklyFlag.ENABLE_CLIENT_REQUESTS_UPDATED_FILTERS,
  );
  doesHaveActiveItemFilters = this._store.selectSignal(ClientRequestsState.doesSelectedRequestHaveActiveItemFilters);
  activeFilterChips$ = this.activeFilters$.pipe(
    map((filters) => {
      if (isEmpty(filters)) {
        return [];
      }

      return Object.entries(filters).flatMap(([filterType, filterValues]) => {
        return Object.entries(filterValues).reduce(
          (acc, [filterValue, isActive]) =>
            isActive
              ? acc.concat({
                  type: convertTextCase(filterType, 'camel', 'title'),
                  value: ClientRequestPanelComponent._getActiveFilterChipLabel(filterType, filterValue),
                  path: [filterType, filterValue],
                })
              : acc,
          [] as Array<{ type: string; value: string; path: [string, string] }>,
        );
      });
    }),
  );

  private static _getActiveFilterChipLabel(type: string, value: string): string {
    if (type === 'status') {
      return ITEM_REQUEST_STATUS_LABEL[value as RequestItemStatusFilterOption] || value;
    } else if (type === 'dueDate') {
      return convertTextCase(value, 'camel', 'title');
    }

    return value;
  }
}
